import { useEffect, useRef } from "react";
import type { CheckResponse, DesiredTone } from "../types/api";
import { AuraBlob } from "./AuraBlob";
import { Feedback } from "./Feedback";
import { RewriteCard } from "./RewriteCard";

type ResultCardProps = {
  response: CheckResponse;
  onAdjust: (tone: DesiredTone) => void;
};

const suggestionIcons = ["✦", "↻", "✓"];

function sendClientEvent(response: CheckResponse, event: "rewrite_copied" | "result_shared") {
  const payload = JSON.stringify({
    request_id: response.request_id,
    event,
    mode: response.result.detected_mode,
    verdict: response.result.verdict.key,
    prompt_version: response.prompt_version
  });
  const blob = new Blob([payload], { type: "application/json" });
  if (navigator.sendBeacon?.("/api/event", blob)) return;
  fetch("/api/event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true
  }).catch(() => undefined);
}

async function shareResult(response: CheckResponse) {
  const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
  const text = response.result.share_text || `${response.result.verdict.label}: ${response.result.headline}`;
  if (navigator.share) {
    await navigator.share({ title: "Unc Check", text, url: siteUrl });
  } else if (navigator.clipboard) {
    await navigator.clipboard.writeText(`${text} ${siteUrl}`);
  }
  sendClientEvent(response, "result_shared");
}

export function ResultCard({ response, onAdjust }: ResultCardProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const result = response.result;

  useEffect(() => {
    headingRef.current?.focus();
  }, [response.request_id]);

  return (
    <section className={`result-card verdict-${result.verdict.key}`} aria-labelledby="result-heading">
      <div className="result-topline">
        <AuraBlob size="result" mood={result.score > 5 ? "spicy" : "sweet"} />
        <div>
          <h2 id="result-heading" ref={headingRef} tabIndex={-1}>
            <span aria-hidden="true">{result.emoji}</span> {result.verdict.label}
          </h2>
          <p className="score-pill">{result.score}/10 unc</p>
        </div>
      </div>
      <h3 className="result-headline">{result.headline}</h3>
      <p>{result.explanation}</p>
      {result.context_note ? <p className="context-note">{result.context_note}</p> : null}
      <section className="suggestions" aria-labelledby="suggestions-heading">
        <h3 id="suggestions-heading">How to recover your aura</h3>
        {result.suggestions.map((suggestion, index) => (
          <div className="suggestion-row" key={suggestion.title}>
            <span aria-hidden="true">{suggestionIcons[index] || "✦"}</span>
            <div>
              <strong>{suggestion.title}</strong>
              <p>{suggestion.detail}</p>
            </div>
          </div>
        ))}
      </section>
      <RewriteCard response={response} onAdjust={onAdjust} onClientEvent={(event) => sendClientEvent(response, event)} />
      <div className="result-actions">
        <button type="button" className="secondary-button" onClick={() => shareResult(response)}>
          Share result
        </button>
      </div>
      <Feedback response={response} />
      <div className="sr-only" aria-live="polite">
        Result ready.
      </div>
    </section>
  );
}
